/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

const requestConfigSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedConfigSuccess.create(response);
};

const requestConfigError = (error: AjaxError): any => {
  return ActionCreators.ReceivedConfigError.create(error.xhr.response);
};

const requestLoginConfigSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedLoginConfigSuccess.create(response);
};

const requestLoginConfigError = (error: AjaxError): any => {
  return ActionCreators.ReceivedLoginConfigError.create(error.xhr.response);
};

const requestCompactExportEnabledSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCompactExportEnabledSuccess.create(response);
};

const requestCompactExportEnabledError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCompactExportEnabledError.create(
    error.message !== undefined ? error.message : 'Error retrieving compact export enabled',
  );
};

export const getConfigEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestConfig.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.getFromBasePath('/config').pipe(
          map((response: AjaxResponse<any>): Action => requestConfigSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestConfigError(error))
              ),
          ),
        ),
    ),
  );

export const getLoginConfigEpic = (
    action$: Observable<Action>,
    state$: StateObservable<RootState>,
    { ajaxRaw }: any,
  ): Observable<Action> =>
    action$.pipe(
      ofType(ActionCreators.RequestLoginConfig.type),
      switchMap(
        ($action: any): Observable<Action> =>
          ajaxRaw.getFromBasePath('/login-config').pipe(
            map((response: AjaxResponse<any>): Action => requestLoginConfigSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(requestLoginConfigError(error))
                ),
            ),
          ),
      ),
    );

export const getCompactExportEnabledEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCompactExportEnabled.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .getWithHeaders('/mpt/config/compactexportenabled', {
            userid: $action.payload.user,
          })
          .pipe(
            map((response: AjaxResponse<any>): Action => requestCompactExportEnabledSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(requestCompactExportEnabledError(error))
                ),
            ),
          ),
    ),
  );
